import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Subject } from 'rxjs';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import Contact from 'src/app/model/Contact/contact';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { Project } from 'src/app/model/project/project';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import {
  IPBoardCustomBoardPhoneNumberPickerHelper,
  PhoneNumberOption
} from './helper/ip-board-custom-board-phone-number-picker-helper';

@Component({
  selector: 'ip-board-custom-board-phone-number-picker',
  templateUrl: './ip-board-custom-board-phone-number-picker.component.html',
  styleUrls: ['./ip-board-custom-board-phone-number-picker.component.scss']
})
// Precondition: project.collaborative is true, therefore project.projectContacts.length <= 1
export class IPBoardCustomBoardPhoneNumberPickerComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input()
  ipBoardPart: IPBoardPart;
  @Input()
  project: Project;
  @Input()
  otherContacts: Contact[];
  @Input()
  leftRightBoard = false;
  @Input()
  label = 'Links';
  @Output()
  save = new EventEmitter<any>();

  phoneNumberOptions: PhoneNumberOption[];
  OTHER_PHONE_NUMBER_ID =
    IPBoardCustomBoardPhoneNumberPickerHelper.OTHER_PHONE_NUMBER_ID;

  selectedPhoneNumberOption: PhoneNumberOption;
  selectedOtherSidePhoneNumberOption: PhoneNumberOption;

  BOARD_TYPE_CHOICES = BoardTypeChoices;

  showOtherSidePicker: boolean = false;

  private emitValueChanged = new Subject<any>();

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit(): void {
    this.ipBoardPart.phoneNumberAdjustment = null;
    this.ipBoardPart.otherPhoneNumberAdjustment = null;

    this.phoneNumberOptions = IPBoardCustomBoardPhoneNumberPickerHelper.getPhoneNumberOptions(
      this.project,
      this.otherContacts
    );

    if (this.ipBoardPart.id != null) {
      this.initializeFromPreviousContent();
    }
    this.initializeSelectedOptions();

    this.initializeSubscriptions();
  }

  private initializeSubscriptions(): void {
    this.addSubscription(
      this.emitValueChanged,
      () => {
        this.projectDisplayObservableService
          .getIPBoardConfigurationChangedSubscribable()
          .next();
      },
      GeneralHelper.noop,
      500
    );

    this.addSubscription(
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable(),
      () => {
        this.phoneNumberOptions = IPBoardCustomBoardPhoneNumberPickerHelper.getPhoneNumberOptions(
          this.project,
          this.otherContacts
        );
        this.initializeSelectedOptions();
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.otherContacts != null) {
      this.phoneNumberOptions = IPBoardCustomBoardPhoneNumberPickerHelper.getPhoneNumberOptions(
        this.project,
        this.otherContacts
      );
      this.initializeSelectedOptions();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.emitValueChanged != null) {
      this.emitValueChanged.complete();
      this.emitValueChanged = null;
    }
  }

  private initializeFromPreviousContent(): void {
    if (this.ipBoardPart.previousPhoneNumberContent != null) {
      this.ipBoardPart.otherPhoneNumber = this.ipBoardPart.previousPhoneNumberContent;
    }
    if (this.ipBoardPart.previousOtherSidePhoneNumberContent != null) {
      this.ipBoardPart.otherSideOtherPhoneNumber = this.ipBoardPart.previousOtherSidePhoneNumberContent;
    }
    this.ipBoardPart.previousPhoneNumberContent = null;
    this.ipBoardPart.previousOtherSidePhoneNumberContent = null;
  }

  private initializeSelectedOptions(): void {
    this.selectedPhoneNumberOption = IPBoardCustomBoardPhoneNumberPickerHelper.getSelectedPhoneNumberOption(
      this.ipBoardPart.otherPhoneNumber,
      this.phoneNumberOptions
    );
    this.selectedOtherSidePhoneNumberOption = IPBoardCustomBoardPhoneNumberPickerHelper.getSelectedPhoneNumberOption(
      this.ipBoardPart.otherSideOtherPhoneNumber,
      this.phoneNumberOptions
    );
    this.showOtherSidePicker =
      this.ipBoardPart.otherPhoneNumber !==
      this.ipBoardPart.otherSideOtherPhoneNumber;
  }

  showOtherSidePickerChanged(): void {
    if (!this.showOtherSidePicker) {
      this.selectedOtherSidePhoneNumberOption = this.selectedPhoneNumberOption;
      this.ipBoardPart.otherSideOtherPhoneNumber = this.ipBoardPart.otherPhoneNumber;
      this.emitValueChanged.next();
      this.save.emit();
    }
  }

  selectedPhoneNumberOptionChanged(): void {
    if (this.selectedPhoneNumberOption == null) {
      return;
    }
    this.ipBoardPart.otherPhoneNumber =
      this.selectedPhoneNumberOption.id ===
      IPBoardCustomBoardPhoneNumberPickerHelper.OTHER_PHONE_NUMBER_ID
        ? null
        : this.selectedPhoneNumberOption.label;
    if (!this.showOtherSidePicker) {
      this.ipBoardPart.otherSideOtherPhoneNumber =
        this.selectedPhoneNumberOption.id ===
        IPBoardCustomBoardPhoneNumberPickerHelper.OTHER_PHONE_NUMBER_ID
          ? null
          : this.selectedPhoneNumberOption.label;
    }
    this.emitValueChanged.next();
    this.save.emit();
  }

  otherPhoneNumberChanged(): void {
    if (!this.showOtherSidePicker) {
      this.ipBoardPart.otherSideOtherPhoneNumber = this.ipBoardPart.otherPhoneNumber;
    }
    this.emitValueChanged.next();
    this.save.emit();
  }

  selectedOtherSidePhoneNumberOptionChanged(): void {
    this.ipBoardPart.otherSideOtherPhoneNumber =
      this.selectedOtherSidePhoneNumberOption.id ===
      IPBoardCustomBoardPhoneNumberPickerHelper.OTHER_PHONE_NUMBER_ID
        ? null
        : this.selectedOtherSidePhoneNumberOption.label;
    this.emitValueChanged.next();
    this.save.emit();
  }

  otherSideOtherPhoneNumberChanged(): void {
    this.emitValueChanged.next();
    this.save.emit();
  }

  compareOptions(o1: PhoneNumberOption, o2: PhoneNumberOption): boolean {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      return o1.id === o2.id;
    } else {
      return false;
    }
  }
}
